import React, { useEffect, useMemo, useState } from "react"
import {
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import { withTranslation } from "react-i18next"
import dayjs from "dayjs"
import Loader from "components/Loader/loader"
import Gauge from "pages/Dashboard/GuageChart"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import { getPreviousSaturday } from "utils/dateUtil"

const gbp = v =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(v)

const gbpTick = value => {
  const sign = value < 0 ? "-£ " : "£ "
  const absValue = Math.abs(value)
  return absValue >= 1000
    ? `${sign}${Math.round(absValue / 1000)}K`
    : `${sign}${absValue}`
}

const MODAL_TITLES = {
  gmp: "🏭 GMP — Good Manufacturing Practice",
  sales: "📈 Sales Revenue — Detailed View",
  oee: "⚙️ OEE — Overall Equipment Effectiveness",
  pl: "💷 Profit & Loss",
}

// ── Same category mapping as Sales Report ──────────────────────────────────
// 4.3 = Bacon | 5.1 = Cured | 2.x = Fresh Pork | 4.5 = Ham
// 3.x = Puddings | 1.x (excl 1.7) = Sausages | 1.7 = Hafners
const getCategoryName = (category, subCategory) => {
  const sc = String(subCategory || "")
  const cat = String(category || "")
  if (sc.startsWith("4.3")) return "Bacon"
  if (sc.startsWith("5.1")) return "Cured"
  if (sc.startsWith("4.5")) return "Ham"
  if (sc.startsWith("1.7")) return "Hafners"
  if (cat === "2") return "Fresh Pork"
  if (cat === "3") return "Puddings"
  if (cat === "1") return "Sausages"
  return null
}

const aggregateRecords = (records, startDate, endDate) => {
  const arr = Array.isArray(records) ? records : Object.values(records || {})
  const start = startDate ? dayjs(startDate).format("YYYY-MM-DD") : null
  const end = endDate ? dayjs(endDate).format("YYYY-MM-DD") : null
  const map = {}
  arr.forEach(r => {
    if (start && end) {
      const d = String(r.date || "").slice(0, 10)
      if (d < start || d > end) return
    }
    const name = getCategoryName(r.category, r.subCategory)
    if (!name) return
    if (!map[name]) map[name] = { category: name, kgs: 0, salesValue: 0 }
    map[name].kgs += Number(r.weeklyWeight || 0)
    map[name].salesValue += Number(r.weeklySalesValue || 0)
  })
  return map
}

const fmtTableGBP = v =>
  "£ " + Math.abs(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })

const pctChange = (curr, prev) => {
  if (!prev) return null
  return (((curr - prev) / prev) * 100).toFixed(0)
}

const DirectorsReport = () => {
  document.title = "Director's Report | Robinsons"

  const [loading, setLoading] = useState(false)
  const [ranges, setRanges] = useState([])
  const [gmp, setGmp] = useState("")
  const [sales, setSales] = useState("")
  const [oee, setOee] = useState("")
  const [netPL, setNetPL] = useState("0")
  const [data, setData] = useState({})
  const [chartModal, setChartModal] = useState({ open: false, type: null })
  const [weekRange, setWeekRange] = useState([])
  const [salesReport, setSalesReport] = useState(null)

  // Same data source and week selection as the Home dashboard (latest uploaded week)
  useEffect(() => {
    const load = async () => {
      const latest = await fetchData(setLoading, "sheet/latest/week")
      if (!latest?.success) return

      const gmpEnd = latest?.data?.gmpDate?.date
      const plEnd = latest?.data?.plDate?.date
      const lowerStartDate =
        new Date(gmpEnd) >= new Date(plEnd) ? gmpEnd : plEnd
      const range = [
        dayjs(getPreviousSaturday(lowerStartDate)),
        dayjs(lowerStartDate),
      ]

      const res = await FetchPostData(setLoading, "sheet/dashboard/data", {
        startDate: range[0].format("YYYY-MM-DD"),
        endDate: range[1].format("YYYY-MM-DD"),
      })
      const d = res?.data
      if (!d?.success) return

      const avg = (arr, key) =>
        arr?.length
          ? arr.reduce((sum, item) => sum + Number(item[key]), 0) / arr.length
          : 0

      setSales(avg(d.salesRevenue, "amount").toFixed(0))
      setGmp(avg(d.gmp, "score").toFixed(0))
      setOee(avg(d.oee, "efficiency").toFixed(0))
      setNetPL(d.finalNetMargin?.toFixed(0))
      setData(d)
      setWeekRange(range)

      // ── Same weekly sales breakdown as Sales Report ──
      // This week = latest uploaded week, Prev week = 7 days before
      try {
        const prevStart = range[0].subtract(7, "day")
        const prevEnd = range[1].subtract(7, "day")
        const salesRes = await FetchPostData(setLoading, "sheet/sales/report", {
          startDate: range[0].format("YYYY-MM-DD"),
          endDate: range[1].format("YYYY-MM-DD"),
          previousStartDate: prevStart.format("YYYY-MM-DD"),
          previousEndDate: prevEnd.format("YYYY-MM-DD"),
        })
        if (salesRes?.data?.success) setSalesReport(salesRes.data)
        else setSalesReport(null)
      } catch (e) {
        setSalesReport(null)
      }
    }

    const loadRanges = async () => {
      const res = await fetchData(setLoading, "gauge/ranges")
      if (res?.success) setRanges(res?.data)
    }

    load()
    loadRanges()
  }, [])

  const colorRanges = i => [
    { limit: ranges?.[i]?.min, color: "#ff3b30" },
    { limit: ranges?.[i]?.mid, color: "#ffcc00" },
    { limit: ranges?.[i]?.max, color: "#34c759" },
  ]

  // Shared gauge config — used by both the card and the expand modal
  const gauges = {
    gmp: {
      min: ranges?.[1]?.start,
      max: ranges?.[1]?.max,
      value: gmp,
      name: "GMP (Good Manufacturing Practice)",
      formatValue: v => `${v} %`,
      ranges: colorRanges(1),
      type: data?.gmp?.length > 1 ? "Average" : "Sum",
    },
    sales: {
      min: ranges?.[0]?.start,
      max: ranges?.[0]?.max,
      value: sales,
      name: "Sales Revenue",
      formatValue: gbp,
      ranges: colorRanges(0),
      formatTickLabel: gbpTick,
      newTickLabel: true,
      type: data?.profitLoss?.length > 1 ? "Average" : "Sum",
    },
    oee: {
      min: ranges?.[2]?.start,
      max: ranges?.[2]?.max,
      value: oee,
      name: "OEE (Overall Equipment Effectiveness)",
      formatValue: v => `${v} %`,
      formatTickLabel: v => `${v} %`,
      newTickLabel: true,
      ranges: colorRanges(2),
      type: data?.oee?.length > 1 ? "Average" : "Sum",
    },
    pl: {
      min: ranges?.[4]?.start,
      max: ranges?.[4]?.max,
      value: netPL,
      name: "Profit & Loss",
      formatValue: gbp,
      ranges: colorRanges(4),
      formatTickLabel: gbpTick,
      newTickLabel: true,
      type: data?.profitLoss?.length > 1 ? "Average" : "Sum",
    },
  }

  const closeModal = () => setChartModal({ open: false, type: null })

  // ── Weekly Sales Breakdown by Category (same logic as Sales Report) ──
  const prevRange = useMemo(() => {
    if (!weekRange?.[0] || !weekRange?.[1]) return []
    return [
      weekRange[0].subtract(7, "day"),
      weekRange[1].subtract(7, "day"),
    ]
  }, [weekRange])

  const tableData = useMemo(() => {
    if (!salesReport) return []
    const allRecords = salesReport.data || salesReport
    const thisMap = aggregateRecords(
      allRecords,
      weekRange?.[0],
      weekRange?.[1]
    )
    const prevMap = aggregateRecords(
      allRecords,
      prevRange?.[0],
      prevRange?.[1]
    )
    const allNames = new Set([
      ...Object.keys(thisMap),
      ...Object.keys(prevMap),
    ])
    return Array.from(allNames).map(name => ({
      category: name,
      kgs: thisMap[name]?.kgs ?? 0,
      lastKgs: prevMap[name]?.kgs ?? 0,
      thisWeek: thisMap[name]?.salesValue ?? 0,
      lastWeek: prevMap[name]?.salesValue ?? 0,
    }))
  }, [salesReport, weekRange, prevRange])

  const totals = useMemo(
    () => ({
      kgs: tableData.reduce((s, r) => s + r.kgs, 0),
      lastKgs: tableData.reduce((s, r) => s + r.lastKgs, 0),
      thisWeek: tableData.reduce((s, r) => s + r.thisWeek, 0),
      lastWeek: tableData.reduce((s, r) => s + r.lastWeek, 0),
    }),
    [tableData]
  )

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content">
        <Row className="g-3 mb-3">
          {Object.keys(gauges).map(key => (
            <Col xl={3} key={key}>
              <div
                className="bg-white rounded-4 shadow"
                style={{ position: "relative", height: "100%", padding: "15px" }}
              >
                <button
                  onClick={() => setChartModal({ open: true, type: key })}
                  title={`Expand ${gauges[key].name}`}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 10,
                    background: "rgba(255,255,255,0.85)",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    padding: "3px 7px",
                    cursor: "pointer",
                    fontSize: "13px",
                    lineHeight: 1,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  }}
                >
                  ⛶
                </button>
                <Gauge {...gauges[key]} />
              </div>
            </Col>
          ))}
        </Row>

        {/* ── Weekly Sales Breakdown by Category (same as Sales Report) ── */}
        <Card
          className="mb-3"
          style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
        >
          <CardBody>
            <h6
              style={{
                fontWeight: 700,
                color: "#1a1a2e",
                marginBottom: "4px",
                fontSize: "14px",
              }}
            >
              Weekly Sales Breakdown by Category
            </h6>
            <p style={{ margin: "0 0 14px", fontSize: "12px", color: "#666" }}>
              {weekRange?.[0]
                ? `This Week: ${weekRange[0].format("DD MMM")} — ${weekRange[1]?.format("DD MMM YYYY")}  |  Prev Week: ${prevRange[0]?.format("DD MMM")} — ${prevRange[1]?.format("DD MMM YYYY")}`
                : "Weekly category breakdown & comparison"}
            </p>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr style={{ background: "#1a1a2e" }}>
                    {[
                      "Category",
                      "This Week Kgs",
                      "Prev Week Kgs",
                      "% Change (Kgs)",
                      "This Week (£)",
                      "Last Week (£)",
                      "% Change (£)",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          padding: "10px 14px",
                          color: "#fff",
                          fontWeight: 700,
                          textAlign: i === 0 ? "left" : "right",
                          whiteSpace: "nowrap",
                          borderBottom: "2px solid #334155",
                          borderRight: i < 6 ? "1px solid #334155" : "none",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, idx) => {
                    const pct = pctChange(row.thisWeek, row.lastWeek)
                    const isPos = pct !== null && Number(pct) >= 0
                    const kgsPct = pctChange(row.kgs, row.lastKgs)
                    const kgsPos = kgsPct !== null && Number(kgsPct) >= 0
                    return (
                      <tr
                        key={idx}
                        style={{
                          background: idx % 2 === 0 ? "#f8fafc" : "#fff",
                        }}
                      >
                        <td
                          style={{
                            padding: "9px 14px",
                            fontWeight: 600,
                            color: "#1a1a2e",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                          }}
                        >
                          {row.category}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            color: "#374151",
                          }}
                        >
                          {row.kgs > 0 ? row.kgs.toLocaleString("en-GB") : "0"}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            color: "#374151",
                          }}
                        >
                          {row.lastKgs > 0
                            ? row.lastKgs.toLocaleString("en-GB")
                            : "0"}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            fontWeight: 700,
                            color:
                              kgsPct === null
                                ? "#9ca3af"
                                : kgsPos
                                ? "#15803d"
                                : "#be123c",
                          }}
                        >
                          {kgsPct !== null
                            ? `${kgsPos ? "▲" : "▼"} ${Math.abs(kgsPct)}%`
                            : "—"}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            fontWeight: 700,
                            color: "#059669",
                          }}
                        >
                          {fmtTableGBP(row.thisWeek)}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            borderRight: "1px solid #e2e8f0",
                            color: "#374151",
                          }}
                        >
                          {row.lastWeek > 0 ? fmtTableGBP(row.lastWeek) : "0"}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            textAlign: "right",
                            borderBottom: "1px solid #e2e8f0",
                            fontWeight: 700,
                            color:
                              pct === null
                                ? "#9ca3af"
                                : isPos
                                ? "#15803d"
                                : "#be123c",
                          }}
                        >
                          {pct !== null
                            ? `${isPos ? "▲" : "▼"} ${Math.abs(pct)}%`
                            : "0"}
                        </td>
                      </tr>
                    )
                  })}
                  {!tableData.length && (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          padding: "16px",
                          textAlign: "center",
                          color: "#9ca3af",
                        }}
                      >
                        No sales data for this week
                      </td>
                    </tr>
                  )}
                </tbody>
                {tableData.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#1e293b" }}>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: "#fff",
                          fontWeight: 800,
                          borderTop: "2px solid #334155",
                        }}
                      >
                        Grand Total
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: "#e2e8f0",
                          fontWeight: 700,
                          textAlign: "right",
                          borderTop: "2px solid #334155",
                        }}
                      >
                        {totals.kgs.toLocaleString("en-GB")}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: "#e2e8f0",
                          fontWeight: 700,
                          textAlign: "right",
                          borderTop: "2px solid #334155",
                        }}
                      >
                        {totals.lastKgs.toLocaleString("en-GB")}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontWeight: 800,
                          textAlign: "right",
                          borderTop: "2px solid #334155",
                          color:
                            totals.kgs >= totals.lastKgs ? "#6ee7b7" : "#fca5a5",
                        }}
                      >
                        {totals.lastKgs
                          ? `${
                              totals.kgs >= totals.lastKgs ? "▲" : "▼"
                            } ${Math.abs(pctChange(totals.kgs, totals.lastKgs))}%`
                          : "—"}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: "#6ee7b7",
                          fontWeight: 800,
                          textAlign: "right",
                          borderTop: "2px solid #334155",
                        }}
                      >
                        {fmtTableGBP(totals.thisWeek)}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color: "#fde68a",
                          fontWeight: 800,
                          textAlign: "right",
                          borderTop: "2px solid #334155",
                        }}
                      >
                        {totals.lastWeek > 0 ? fmtTableGBP(totals.lastWeek) : "—"}
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          color:
                            totals.thisWeek >= totals.lastWeek
                              ? "#6ee7b7"
                              : "#fca5a5",
                          fontWeight: 800,
                          textAlign: "right",
                          borderTop: "2px solid #334155",
                        }}
                      >
                        {totals.lastWeek
                          ? `${
                              totals.thisWeek >= totals.lastWeek ? "▲" : "▼"
                            } ${Math.abs(
                              pctChange(totals.thisWeek, totals.lastWeek)
                            )}%`
                          : "—"}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardBody>
        </Card>
      </div>

      <Modal isOpen={chartModal.open} toggle={closeModal} centered size="lg">
        <ModalHeader
          toggle={closeModal}
          style={{
            color: "#000",
            borderBottom: "1px solid #e8e8f0",
            fontWeight: 700,
            fontSize: "15px",
          }}
          className="text-dark"
        >
          {MODAL_TITLES[chartModal.type]}
        </ModalHeader>
        <ModalBody style={{ padding: "24px", background: "#fff" }}>
          {chartModal.type && (
            <Gauge {...gauges[chartModal.type]} width={600} />
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default withTranslation()(DirectorsReport)
